import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { TrendingUp } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";

function fmtUSD(v) {
  return `$${Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

function CustomTooltip({ active, payload, label }) {
  const { t } = useI18n();
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-2.5 text-xs">
      <div className="font-bold text-slate-800 mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 mt-0.5">
          <span
            className="w-2.5 h-2.5 rounded-sm"
            style={{ backgroundColor: p.color }}
          />
          <span className="text-slate-600 capitalize">
            {t({ revenue: "Receita", cost: "Custos", profit: "Lucro" }[p.dataKey] || p.dataKey)}
          </span>
          <span className="font-semibold text-slate-900 ml-auto">
            ${Number(p.value).toFixed(2)}
          </span>
        </div>
      ))}
    </div>
  );
}

export function FinanceTrendChart({ months: initialMonths = 6 }) {
  const { t } = useI18n();
  const [months, setMonths] = useState(() => {
    try {
      const saved = parseInt(localStorage.getItem("cm:finance:months"), 10);
      return Number.isFinite(saved) && saved > 0 ? saved : initialMonths;
    } catch {
      return initialMonths;
    }
  });
  const [data, setData] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
    api
      .get(`/finance/monthly-trend?months=${months}`)
      .then(({ data }) => {
        setData(data.months || []);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
    try {
      localStorage.setItem("cm:finance:months", String(months));
    } catch {
      /* ignore */
    }
  }, [months]);

  // Resumo: variação % vs mês anterior + total recente
  const last = data[data.length - 1] || {};
  const prev = data[data.length - 2] || {};
  const variation =
    prev.revenue && prev.revenue > 0
      ? Math.round(((last.revenue - prev.revenue) / prev.revenue) * 100)
      : null;

  const presets = [
    { value: 1, label: t("30 dias") },
    { value: 3, label: "3m" },
    { value: 6, label: "6m" },
    { value: 12, label: "12m" },
    { value: 24, label: "24m" },
  ];

  return (
    <div className="cm-card p-4 sm:p-5" data-testid="finance-trend-chart">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold flex items-center gap-1.5">
            <TrendingUp size={13} />{" "}
            {months === 1
              ? t("Tendência financeira · últimos 30 dias")
              : t("Tendência financeira · últimos {n} meses", { n: months })}
          </div>
          <div className="text-lg sm:text-xl font-bold text-slate-900 mt-0.5">
            {fmtUSD(last.revenue || 0)}
            <span className="text-xs font-normal text-slate-500 ml-2">
              {t("em {m}", { m: last.label || "—" })}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {variation !== null && (
            <span
              className={`text-xs font-bold px-2 py-1 rounded-full ${
                variation >= 0
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}
              data-testid="finance-trend-variation"
            >
              {variation >= 0 ? "↑" : "↓"} {Math.abs(variation)}% {t("vs mês anterior")}
            </span>
          )}
        </div>
      </div>

      {/* Range presets */}
      <div className="inline-flex flex-wrap gap-1 mb-3 bg-slate-100 p-1 rounded-lg" data-testid="finance-trend-range">
        {presets.map((p) => (
          <button
            key={p.value}
            type="button"
            data-testid={`finance-trend-range-${p.value}`}
            onClick={() => setMonths(p.value)}
            className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
              months === p.value
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="h-[220px] sm:h-[280px]">
        {!loaded ? (
          <div className="h-full flex items-center justify-center text-slate-400 text-sm">
            {t("Carregando…")}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#64748b" }}
                axisLine={{ stroke: "#cbd5e1" }}
                tickLine={false}
              />
              <YAxis
                tickFormatter={fmtUSD}
                tick={{ fontSize: 11, fill: "#64748b" }}
                axisLine={false}
                tickLine={false}
                width={60}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f1f5f9", opacity: 0.5 }} />
              <Legend
                wrapperStyle={{ fontSize: "12px", paddingTop: "8px" }}
                formatter={(v) =>
                  t({ revenue: "Receita", cost: "Custos", profit: "Lucro" }[v] || v)
                }
              />
              <Bar
                dataKey="revenue"
                fill="#10b981"
                radius={[4, 4, 0, 0]}
                maxBarSize={36}
              />
              <Bar
                dataKey="cost"
                fill="#f43f5e"
                radius={[4, 4, 0, 0]}
                maxBarSize={36}
              />
              <Line
                type="monotone"
                dataKey="profit"
                stroke="#2563eb"
                strokeWidth={2.5}
                dot={{ r: 4, fill: "#2563eb" }}
                activeDot={{ r: 6 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
